import React, { useEffect, useMemo, useState } from "react";
import {
  BellRing,
  CheckCircle2,
  ClipboardCheck,
  Package,
  RefreshCw,
  Store,
  Truck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  FoodNotificationsService,
  isFoodPanelNotification,
  mapNotificationToRoute,
} from "../../services/notifications.service";
import { connectSocket } from "../../services/notificationsSocket";
import { getAuthUser } from "../AdminAuth/components/authStore";

function timeAgo(value) {
  if (!value) return "";
  const date = new Date(value);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);

  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} day ago`;
}

function isUnread(item) {
  return !(item?.is_read || item?.isRead);
}

function getTitle(item) {
  return item?.title || item?.event_type || item?.type || "Notification";
}

function getMessage(item) {
  return item?.message || item?.body || item?.description || "Dashboard action updated.";
}

function getDisplayMessage(item) {
  return item?.meta?.displayAction || getMessage(item);
}

function getTypeBucket(item = {}) {
  const text = `${item?.type || ""} ${item?.title || ""} ${item?.body || ""} ${item?.message || ""}`.toLowerCase();
  if (text.includes("order") || text.includes("driver") || text.includes("delivery") || text.includes("payment") || text.includes("refund")) return "orders";
  if (text.includes("approval") || text.includes("request")) return "approvals";
  if (text.includes("restaurant") || text.includes("employee") || text.includes("availability")) return "restaurants";
  if (text.includes("food") || text.includes("combo") || text.includes("menu")) return "items";
  return "all";
}

function getIcon(item) {
  const bucket = getTypeBucket(item);
  if (bucket === "orders") return Truck;
  if (bucket === "approvals") return ClipboardCheck;
  if (bucket === "restaurants") return Store;
  if (bucket === "items") return Package;
  return BellRing;
}

function FilterButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-[12px] font-semibold transition ${
        active
          ? "bg-[#d86a78] text-white"
          : "border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff6f7]"
      }`}
    >
      {children}
    </button>
  );
}

function NotificationCard({ item, onOpen, onMarkRead }) {
  const Icon = getIcon(item);
  const unread = isUnread(item);
  const meta = item?.meta && typeof item.meta === "object" ? item.meta : {};

  return (
    <div
      className={`rounded-[18px] border p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        unread ? "border-[#f2c6cc] bg-[#fff8f9]" : "border-[#f2c6cc] bg-white"
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] bg-[#fff1f3] text-[#d86a78]">
          <Icon size={21} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <button type="button" onClick={() => onOpen(item)} className="min-w-0 flex-1 text-left">
              <h3 className="truncate text-[15px] font-semibold text-black">{getTitle(item)}</h3>
              <p className="mt-1 text-[13px] leading-5 text-slate-500">{getDisplayMessage(item)}</p>
              {meta?.actorName || meta?.actorEmail || meta?.restaurantName || meta?.orderNumber ? (
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-400">
                  {meta?.restaurantName ? <span>{meta.restaurantName}</span> : null}
                  {meta?.orderNumber ? <span>#{meta.orderNumber}</span> : null}
                  {meta?.actorName ? <span>By {meta.actorName}</span> : null}
                  {meta?.actorEmail ? <span>{meta.actorEmail}</span> : null}
                </div>
              ) : null}
            </button>

            <div className="flex shrink-0 items-center gap-2">
              {unread ? (
                <span className="rounded-full bg-[#d86a78] px-3 py-1 text-[10px] font-semibold uppercase text-white">New</span>
              ) : (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-semibold uppercase text-slate-500">Read</span>
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[12px] text-slate-400">{timeAgo(item?.created_at || item?.createdAt)}</span>

            <div className="flex flex-wrap gap-2">
              {unread ? (
                <button
                  type="button"
                  onClick={() => onMarkRead(item)}
                  className="inline-flex items-center gap-2 rounded-[10px] border border-[#f2c6cc] bg-white px-3 py-2 text-[12px] font-semibold text-slate-600 hover:bg-[#fff6f7] hover:text-[#d86a78]"
                >
                  <CheckCircle2 size={14} />
                  Mark read
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => onOpen(item)}
                className="inline-flex items-center gap-2 rounded-[10px] border border-[#f2c6cc] bg-white px-3 py-2 text-[12px] font-semibold text-[#d86a78] hover:bg-[#fff6f7]"
              >
                View details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FoodNotificationsPage() {
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const user = useMemo(() => getAuthUser() || {}, []);
  const navigate = useNavigate();

  const unreadCount = items.filter(isUnread).length;

  async function loadNotifications() {
    try {
      setLoading(true);
      const res = await FoodNotificationsService.list(user, { limit: 200, offset: 0 });
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load food notifications:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleMarkRead(item) {
    if (!item?.id) return;
    try {
      await FoodNotificationsService.markRead(item.id, user);
      setItems((prev) => prev.map((row) => (row.id === item.id ? { ...row, is_read: true, isRead: true } : row)));
    } catch (err) {
      console.error("Failed to mark food notification read:", err);
    }
  }

  async function handleMarkAllRead() {
    try {
      setMarkingAll(true);
      await FoodNotificationsService.markAllRead(user, items);
      setItems((prev) => prev.map((item) => ({ ...item, is_read: true, isRead: true })));
    } catch (err) {
      console.error("Failed to mark all food notifications read:", err);
    } finally {
      setMarkingAll(false);
    }
  }

  async function handleOpen(item) {
    if (item?.id && isUnread(item)) {
      await handleMarkRead(item);
    }
    navigate(mapNotificationToRoute(item) || "/admin/food-dashboard");
  }

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    const socket = connectSocket();
    if (!socket) return undefined;

    const handleNotification = (data) => {
      if (!isFoodPanelNotification(data)) return;
      setItems((prev) => [data, ...prev.filter((item) => item?.id !== data?.id)]);
    };

    socket.on("notification", handleNotification);
    return () => socket.off("notification", handleNotification);
  }, []);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (filter === "unread" && !isUnread(item)) return false;
      if (filter === "read" && isUnread(item)) return false;
      if (typeFilter !== "all" && getTypeBucket(item) !== typeFilter) return false;
      return true;
    });
  }, [items, filter, typeFilter]);

  return (
    <div className="space-y-6">
      <div className="rounded-[22px] border border-[#f2c6cc] bg-gradient-to-r from-[#fff6f7] via-white to-[#fff9f2] p-5 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#d86a78] shadow-sm">
              <BellRing size={14} />
              Notifications
            </div>

            <h1 className="mt-3 text-[24px] font-bold leading-tight text-black sm:text-[32px]">Notification Center</h1>

            <p className="mt-1 max-w-3xl text-[13px] text-slate-500">
              Live alerts for restaurants, catalog updates, approvals, payments, refunds, and order actions.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadNotifications}
              className="inline-flex items-center justify-center gap-2 rounded-[12px] border border-[#f2c6cc] bg-white px-4 py-2 text-[12px] font-semibold text-[#d86a78] hover:bg-[#fff6f7]"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={markingAll || unreadCount === 0}
              className="inline-flex items-center justify-center gap-2 rounded-[12px] bg-[#d86a78] px-4 py-2 text-[12px] font-semibold text-white hover:bg-[#c95b69] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <CheckCircle2 size={15} />
              {markingAll ? "Reading..." : "Mark all as read"}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5 shadow-sm">
          <div className="text-[26px] font-bold text-black">{items.length}</div>
          <div className="mt-1 text-[12px] font-medium uppercase text-slate-500">Total food notifications</div>
        </div>

        <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5 shadow-sm">
          <div className="text-[26px] font-bold text-black">{unreadCount}</div>
          <div className="mt-1 text-[12px] font-medium uppercase text-slate-500">Unread alerts</div>
        </div>

        <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5 shadow-sm">
          <div className="text-[26px] font-bold text-black">Live</div>
          <div className="mt-1 text-[12px] font-medium uppercase text-slate-500">Module source</div>
        </div>
      </div>

      <div className="rounded-[22px] border border-[#f2c6cc] bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="text-[18px] font-bold text-black">Recent food alerts</h2>
            <p className="text-[12px] text-slate-400">Filtered to food dashboard only, not grocery/warehouse alerts.</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>All</FilterButton>
            <FilterButton active={filter === "unread"} onClick={() => setFilter("unread")}>Unread</FilterButton>
            <FilterButton active={filter === "read"} onClick={() => setFilter("read")}>Read</FilterButton>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <FilterButton active={typeFilter === "all"} onClick={() => setTypeFilter("all")}>All actions</FilterButton>
          <FilterButton active={typeFilter === "orders"} onClick={() => setTypeFilter("orders")}>Orders</FilterButton>
          <FilterButton active={typeFilter === "approvals"} onClick={() => setTypeFilter("approvals")}>Approvals</FilterButton>
          <FilterButton active={typeFilter === "restaurants"} onClick={() => setTypeFilter("restaurants")}>Restaurants</FilterButton>
          <FilterButton active={typeFilter === "items"} onClick={() => setTypeFilter("items")}>Catalog Items</FilterButton>
        </div>

        <div className="mt-5 space-y-4">
          {loading ? (
            <div className="rounded-[18px] border border-dashed border-[#f2c6cc] p-10 text-center text-sm text-slate-400">Loading food notifications...</div>
          ) : filtered.length ? (
            filtered.map((item) => (
              <NotificationCard key={item.id || `${item.type}-${item.created_at || item.createdAt}`} item={item} onOpen={handleOpen} onMarkRead={handleMarkRead} />
            ))
          ) : (
            <div className="rounded-[18px] border border-dashed border-[#f2c6cc] p-10 text-center text-sm text-slate-400">No food notifications found</div>
          )}
        </div>
      </div>
    </div>
  );
}
